'use client';

import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { PageHeader } from '@/components/ui/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useApplePayIntegrator, useApplePayPlatformDomains } from '@/lib/api/apple-pay-platform';

import { IntegratorPanel } from './components/IntegratorPanel';
import { AddDomainDialog } from './components/AddDomainDialog';
import { DomainsTable } from './components/DomainsTable';
import { HowToAddDomain } from './components/HowToAddDomain';

/**
 * Staff-only operations page for Apple Pay under Tagada's payment platform
 * integrator ID (PPIID).
 *
 * Its own route rather than another account-manager tab: this is platform-wide
 * configuration spanning every merchant, not per-account settings.
 *
 * Production only. Apple does not enforce domain verification in its sandbox,
 * so a sandbox pass proves wiring and nothing about the file — an environment
 * switcher here would mostly offer a way to be misled. The schema still carries
 * `environment`, so restoring the choice later is a UI change alone.
 */
const ENVIRONMENT = 'production' as const;

export default function ManageApplePayPage() {
  const { t } = useTranslation('manageApplePay');
  const { isAccountManager, isLoading: isLoadingRole } = useAccountManager();
  const { isSuperAdmin } = useSessionClaims();

  const canAccess = isAccountManager || isSuperAdmin;

  const {
    data: integrator,
    isLoading: isLoadingIntegrator,
    error: integratorError,
  } = useApplePayIntegrator(ENVIRONMENT);
  const {
    data: domains,
    isLoading: isLoadingDomains,
    error: domainsError,
  } = useApplePayPlatformDomains(ENVIRONMENT);

  // A failed query and an unconfigured integrator both yield no data and look
  // identical on screen — "nothing here yet" when the real answer is "the
  // backend is erroring", usually an unapplied migration. Say which.
  const loadError = integratorError ?? domainsError;

  if (isLoadingRole) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t('page.checkingPermissions')}</span>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-semibold">{t('page.accessDeniedTitle')}</h2>
        <p className="text-muted-foreground">{t('page.accessDeniedDescription')}</p>
      </div>
    );
  }

  // Domains can only be added once the association file exists — Apple fetches
  // it during registration, so offering the button earlier would only produce a
  // rejection the operator can't act on.
  const isIntegratorReady = !!integrator && integrator.status !== 'disabled' && !!integrator.associationFileContent;

  return (
    <div className="space-y-6">
      <PageHeader title={t('page.title')} description={t('page.description')} />

      {loadError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('page.loadFailedTitle')}</AlertTitle>
          <AlertDescription>
            <p>{t('page.loadFailed')}</p>
            <p className="mt-1 font-mono text-xs">{loadError.message}</p>
          </AlertDescription>
        </Alert>
      )}

      {isLoadingIntegrator ? (
        <Card>
          <CardContent className="flex h-40 items-center justify-center">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </CardContent>
        </Card>
      ) : (
        <IntegratorPanel environment={ENVIRONMENT} integrator={integrator ?? null} />
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle>{t('domains.title')}</CardTitle>
              <CardDescription>{t('domains.description')}</CardDescription>
            </div>
            <AddDomainDialog environment={ENVIRONMENT} disabled={!isIntegratorReady} />
          </div>
        </CardHeader>
        <CardContent>
          {!isIntegratorReady ? (
            <p className="text-muted-foreground py-6 text-center text-sm">{t('domains.blocked')}</p>
          ) : isLoadingDomains ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DomainsTable domains={domains ?? []} />
          )}
        </CardContent>
      </Card>

      <HowToAddDomain associationFileContent={integrator?.associationFileContent} />
    </div>
  );
}
